// Copyright (C) 2019 TANNER AG

import React, { useContext, useEffect } from "react";
import { compose }  from "recompose";
import * as PropTypes from "prop-types";
import { Collapse, withStyles } from "@material-ui/core";
import { Button } from "../../components/button";
import classnames from "classnames";
import { TranslationContext } from "../../translation";
import { withRouter } from "react-router-dom";
import { withStore } from "../../store/withstore";
import ScrollableAnchor, { goToAnchor, configureAnchors } from "react-scrollable-anchor";

const ModuleCollapse = withStyles({
	container: {
		boxShadow: "0 1px 0 0 rgba(48, 60, 73, 0.15)"
	}
})(Collapse);

let CalculationButton = ({ disabled, history, onContinue, store }) => {
	const { translate } = useContext(TranslationContext);
	return (
		<Button
			variant="contained"
			size="large"
			disabled={disabled}
			style={{ minWidth: 265 }}
			onClick={() => {
				onContinue();
				history.push("/tco-lcc/result");
			}}
		>
			{translate("common.calculate", { type: store.configuration })}
		</Button>
	);
};

CalculationButton = compose(
	withRouter,
	withStore
)(CalculationButton);

const styles = {
	labelSection: {
		fontFamily: "MANEurope, Arial, sans-serif",
		color: "#303C49",
		height: "60px",
		display: "flex",
		justifyContent: "space-around",
		alignItems: "center",
		opacity: 1,
		transition: "opacity 0.3s ease-out",
		"&.disabled": {
			color: "#e2e3e4"
		},
		"&.active": {
			opacity: 0
		}
	},
	labelTitle: {
		fontWeight: "bold",
		fontStretch: "condensed",
		fontSize: "18px",
		lineHeight: "25px",
		textTransform: "uppercase",
		textAlign: "center",
		minWidth: 200
	},
	labelValue: {
		fontSize: "15px",
		lineHeight: "22px",
		fontWeight: 300,
		fontStyle: "normal",
		textAlign: "center",
		minWidth: 200
	},
	title: {
		fontFamily: "MANEurope, Arial, sans-serif",
		fontWeight: 300,
		fontStyle: "normal",
		fontSize: "32px",
		lineHeight: "35px",
		textAlign: "center",
		color: "#303c49"
	},
	queryContainerWrapper: {
		position: "relative",
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		height: "100%",
		marginTop: "25px"
	},
	queryContainer: {
		flexBasis: 0,
		flexGrow: 1,
		width: "100%",
		maxWidth: "100%",
		"& > div": {
			display: "flex",
			alignItems: "center",
			padding: "23px 0"
		},
		"&:first-child > div": {
			justifyContent: "flex-end",
			paddingRight: 100
		},
		"&:nth-child(2)": {
			borderLeft: "1px solid #e2e3e4",
			"& > div": {
				paddingLeft: 100,
			}
		},
		"&:only-child > div": {
			justifyContent: "center",
			paddingRight: 0,
			flexDirection: "row",
		}
	},
	actionContainer: {
		display: "flex",
		justifyContent: "center",
		margin: "40px 0"
	}
};

configureAnchors({
	offset: -500,
	scrollDuration: 500
});

export const Module = withStyles(styles)((props) => {
	const {
		classes,
		id,
		activeModule,
		nextModule,
		onActivate,
		label,
		title,
		valueA,
		valueB,
		queryA,
		queryB,
		continueEnabled,
		onContinue,
		final
	} = props;
	const active = activeModule === id;
	const disabled = Boolean(!valueA || !valueB);
	const { translate } = useContext(TranslationContext);

	useEffect(() => {
		if (active) {
			goToAnchor(id, false);
		}
	}, [active]);

	return (
		<ModuleCollapse in={active} collapsedHeight="60px">
			<ScrollableAnchor id={id}>
				<div
					onClick={() => (disabled || active) ? null : onActivate(id)}
					className={classnames(classes.labelSection, { active, disabled })}
				>
					<span className={classes.labelValue}>{valueA}</span>
					<span className={classes.labelTitle}>{translate(label)}</span>
					<span className={classes.labelValue}>{valueB}</span>
				</div>
			</ScrollableAnchor>
			<div>
				<div
					className={classes.title}
					dangerouslySetInnerHTML={{ __html: translate(title) }}
				/>
				<div className={classes.queryContainerWrapper}>
					<div className={classes.queryContainer}>
						<div>{queryA}</div>
					</div>
					{queryB && (
						<div className={classes.queryContainer}>
							<div>{queryB}</div>
						</div>
					)}
				</div>
				<div className={classes.actionContainer}>
					{!final && (
						<Button
							variant="outlined"
							size="large"
							disabled={!continueEnabled}
							style={{ minWidth: 265 }}
							onClick={() => {
								if (onContinue) {
									onContinue();
								}

								if (nextModule) {
									onActivate(nextModule);
								}
							}}
						>
							{translate("common.continue")}
						</Button>
					)}
					{final && (
						<CalculationButton disabled={!continueEnabled} onContinue={onContinue}/>
					)}
				</div>
			</div>
		</ModuleCollapse>
	);
});

Module.propTypes = {
	id: PropTypes.string,
	activeModule: PropTypes.string,
	nextModule: PropTypes.string,
	onActivate: PropTypes.func,
	label: PropTypes.string,
	title: PropTypes.any,
	valueA: PropTypes.string,
	valueB: PropTypes.string,
	queryA: PropTypes.any,
	queryB: PropTypes.any,
	continueEnabled: PropTypes.bool,
	onContinue: PropTypes.func,
	final: PropTypes.bool
};



// WEBPACK FOOTER //
// ./src/tco/configuration/module/index.js